import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { ArrowLeft, CheckIcon, RefreshCw, AlertCircle, ChevronDown } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAssessment } from './AssessmentContext';
import { decodedToken } from '../../utils/auth';
import { getLatestChangeListForVerification, getImportChangeListById } from '../../services/apiService';


const HWChangeVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userId, setUserId] = useState(null);
  const [loadingError, setLoadingError] = useState('');
  const [selectedSubType, setSelectedSubType] = useState({});
  const [showSubTypeDropdown, setShowSubTypeDropdown] = useState({});
  
  const {
    currentTab,
    setCurrentTab,
    setUserId: setContextUserId,
    setChangeList,
    setSelectedChanges,
    setReferenceModel,
    setCompletedItem,
    getCurrentTabData,
    setDataLoadAttempted,
    getTabData,
    isAllCompleted,
    hasChangeList,
    isDataLoaded,
    isLoading,
    hasDataLoadAttempted,
    getDataSource,
    clearAllData,
    loadChangeListData
  } = useAssessment();

  const tabs = [
    { id: 'TV', label: 'TV' },
    { id: 'MNT', label: 'MNT' },
    { id: 'PD', label: 'PD', hasSubTypes: true },
    { id: 'AVA', label: 'AVA', hasSubTypes: true },
  ];

  // Sub-product types configuration
  const subProductTypes = {
    PD: [
      { id: 'PD_Signage & IWB', label: 'PD_Signage & IWB' },
      { id: 'PD_RGB LED', label: 'PD_RGB LED' },
      { id: 'PD_AIO', label: 'PD_AIO' },
      { id: 'PD_EPD', label: 'PD_EPD' }
    ],
    AVA: [
      { id: 'AVA_Audio', label: 'AVA_Audio' },
      { id: 'AVA_Headphone', label: 'AVA_Headphone' }
    ]
  };

  const assessmentItems = ['EE', 'PWR', 'ME', 'SW', 'LCM'];
  const currentTabData = getCurrentTabData();

  // Get current sub-type for the active tab
  const currentSubType = useMemo(() => {
    if (!tabs.find(t => t.id === currentTab)?.hasSubTypes) {
      return null;
    }
    return selectedSubType[currentTab] || subProductTypes[currentTab]?.[0]?.id;
  }, [currentTab, selectedSubType]);

  // Check if at least one change is selected in the current tab
  const hasSelectedChanges = useMemo(() => {
    if (!currentTabData?.selectedChanges) return false;
    
    return assessmentItems.some(item => {
      const itemChanges = currentTabData.selectedChanges[item] || [];
      return itemChanges.length > 0;
    });
  }, [currentTabData?.selectedChanges, assessmentItems]);

  // Calculate if review button should be enabled
  const isReviewEnabled = useMemo(() => {
    const allCompleted = isAllCompleted(currentTab);
    const dataLoaded = isDataLoaded(currentTab);
    const loading = isLoading(currentTab);
    
    // Check if any assessment item has data
    const hasAnyData = assessmentItems.some(item => {
      if (!currentTabData?.changeListData) return false;
      const itemData = currentTabData.changeListData[item];
      return itemData && Array.isArray(itemData) && itemData.length > 0;
    });
    
    const canReview = dataLoaded && !loading && hasAnyData;
    
    // Disable ONLY when all completed but no changes selected
    if (allCompleted && !hasSelectedChanges) {
      return false;
    }
    
    return canReview;
  }, [currentTab, isAllCompleted, isDataLoaded, isLoading, hasSelectedChanges, currentTabData?.changeListData, assessmentItems]);

  // Check if an assessment item has data
  const hasItemData = useCallback((item) => {
    if (!currentTabData?.changeListData) return false;
    
    const itemData = currentTabData.changeListData[item];
    return itemData && Array.isArray(itemData) && itemData.length > 0;
  }, [currentTabData?.changeListData]);

  // Get userId from JWT token
  useEffect(() => {
    const getUserId = () => {
      try {
        const decoded = decodedToken();
        if (decoded && decoded.id) {
          return decoded.id;
        }
        console.warn('No valid JWT token or id found');
        return null;
      } catch (error) {
        console.error('Error decoding JWT token:', error);
        return null;
      }
    };

    const currentUserId = getUserId();
    if (currentUserId) {
      console.log('Setting user ID from JWT:', currentUserId);
      setUserId(currentUserId);
      setContextUserId(currentUserId);
    } else {
      console.warn('No user ID found in JWT token');
      setLoadingError('User authentication required');
    }
  }, [setContextUserId]);

  // Initialize default sub-types
  useEffect(() => {
    const newSelectedSubType = {};
    tabs.forEach(tab => {
      if (tab.hasSubTypes && subProductTypes[tab.id]) {
        if (!selectedSubType[tab.id]) {
          newSelectedSubType[tab.id] = subProductTypes[tab.id][0].id;
        }
      }
    });
    if (Object.keys(newSelectedSubType).length > 0) {
      setSelectedSubType(prev => ({ ...prev, ...newSelectedSubType }));
    }
  }, []);

useEffect(() => {
  const loadDataForTab = async () => {
    if (!userId || !currentTab) return;

    // Determine the actual product type to fetch (sub-type or base type)
    const effectiveProductType = currentSubType || currentTab;
    
    console.log(`Loading data for tab: ${currentTab}, effective type: ${effectiveProductType}`);

    // ---- 1. Already loaded or already attempted? → bail out
    if (hasChangeList(currentTab) || hasDataLoadAttempted(currentTab)) {
      console.log(`Data already loaded/attempted for ${currentTab}`);
      return;
    }

    setLoadingError('');
    try {
      const decoded   = decodedToken();
      const userEmail = decoded?.email ?? '';
      const userType  = decoded?.userType ?? 'Admin';

      if (!userEmail || !userType) throw new Error('User email and type required');

      // ---- 2. ALWAYS call the “latest for verification” endpoint
      const result = await getLatestChangeListForVerification(effectiveProductType, userEmail, userType);

      if (!result.success || !result.data) {
        // No upload yet → just mark attempted (no error UI)
        setDataLoadAttempted(currentTab, true);
        return;
      }

      const { metadata, data } = result;

      // ---- 3. Pre-select rows that have applied === 'YES'
      const preSelectedChanges = {};
      assessmentItems.forEach(item => {
        const section = data[item] ?? [];
        const preSel = section
          .filter(r => r.applied === 'YES')
          .map(r => ({ id: r.id, detail: r.detail, applied: true }));
        if (preSel.length) preSelectedChanges[item] = preSel;
      });

      // ---- 4. Store everything in the context
      setChangeList(currentTab, metadata.id, data, preSelectedChanges, 'hardware', effectiveProductType);
    } catch (err) {
      console.error(`Error loading data for ${effectiveProductType}:`, err);
      setLoadingError(`Failed to load data: ${err.message}`);
      setDataLoadAttempted(currentTab, true);          // ← no error banner for “no data”
    }
  };

  // tiny debounce so the effect does not fire twice on strict-mode double-mount
  const tid = setTimeout(loadDataForTab, 100);
  return () => clearTimeout(tid);
}, [
  userId,
  currentTab,
  currentSubType, 
  hasChangeList,
  hasDataLoadAttempted,
  setDataLoadAttempted,
  setChangeList,
  assessmentItems
]);

  // Handle navigation from import (only for new imports)
  useEffect(() => {
    const { state } = location;
    if (!state) return;

    const { 
      selectedChanges: newSelectedChanges,
      activeTab: newTab = 'TV',
      referenceModels: newReferenceModels,
      changeListId,
      completedItems = [],
      returnFromImport = false,
      subType: incomingSubType
    } = state;

    console.log('Navigation state:', { newTab, changeListId, returnFromImport,incomingSubType });

  if (incomingSubType && tabs.find(t => t.id === newTab)?.hasSubTypes) {
    console.log(`Setting sub-type for ${newTab}: ${incomingSubType}`);
    setSelectedSubType(prev => ({ ...prev, [newTab]: incomingSubType }));
  }

    if (newTab !== currentTab) {
      setCurrentTab(newTab);
    }

    // Load from ImportChangeList when explicitly coming from import
    if (returnFromImport && changeListId) {
      console.log(`Loading from ImportChangeList: ${changeListId} for tab ${newTab}`);
      
      const loadFromImport = async () => {
        try {
          const decoded = decodedToken();
          const userEmail = decoded?.email ?? '';
          const userType = decoded?.userType ?? 'Admin';
          
          // Load from ImportChangeList collection
          const result = await getImportChangeListById(changeListId, userEmail, userType);
          
          if (result.success && result.data) {
            const { metadata, data } = result;
            
            // Pre-select rows that have applied === 'YES'
            const preSelectedChanges = {};
            assessmentItems.forEach(item => {
              const section = data[item] ?? [];
              const preSel = section
                .filter(r => r.applied === 'YES')
                .map(r => ({ id: r.id, detail: r.detail, applied: true }));
              if (preSel.length) preSelectedChanges[item] = preSel;
            });
            
            // Store with 'import' as source
            setChangeList(newTab, metadata.id, data, preSelectedChanges, 'import', incomingSubType);
            console.log(`✅ Successfully loaded from ImportChangeList for ${newTab}`);
          }
        } catch (error) {
          console.error('Failed to load from ImportChangeList:', error);
          setLoadingError(`Failed to load imported data: ${error.message}`);
        }
      };
      
      loadFromImport();
    }

    if (newSelectedChanges && Object.keys(newSelectedChanges).length > 0) {
      setSelectedChanges(newTab, newSelectedChanges);
    }

    if (newReferenceModels && Object.keys(newReferenceModels).length > 0) {
      Object.entries(newReferenceModels).forEach(([item, value]) => {
        setReferenceModel(newTab, item, value);
      });
    }

    completedItems.forEach(item => {
      setCompletedItem(newTab, item);
    });
  }, [location.state]);

  const handleTabChange = (tabId) => {
    if (tabId !== currentTab) {
      console.log(`Switching from ${currentTab} to ${tabId}`);
      setCurrentTab(tabId);
      setLoadingError('');
      
    // Show dropdown automatically for tabs with sub-types
      if (tabs.find(t => t.id === tabId)?.hasSubTypes) {
        setShowSubTypeDropdown(prev => ({ ...prev, [tabId]: true }));
      }
    }
  };

const handleSubTypeChange = (tabId, subTypeId) => {
  console.log(`Changing sub-type for ${tabId} to ${subTypeId}`);
  
  setSelectedSubType(prev => ({ ...prev, [tabId]: subTypeId }));
  setShowSubTypeDropdown(prev => ({ ...prev, [tabId]: false }));
  
  // Clear existing data for this tab to force reload with new sub-type
  clearAllData(); // Or add a method like clearTabData(tabId) in your context
  
  // Reset data load attempted flag to allow reloading
  setDataLoadAttempted(tabId, false);
};


  const handleItemClick = (item) => {
    if (!isDataLoaded(currentTab)) {
      alert('Please import a change list first.');
      return;
    }
    
    navigate(`/assessment/${currentTab}/${item}`, {
      state: { 
        selectedChanges: currentTabData.selectedChanges,
        referenceModels: currentTabData.referenceModels,
        changeListId: currentTabData.changeListId,
        completedItems: Array.from(currentTabData.completedItems),
        subType: currentSubType
      }
    });
  };

  const handleBackClick = () => {
    clearAllData();
    navigate('/dashboard');
  };

  const handleReviewClick = () => {
    // Double-check conditions before navigating
    if (!isReviewEnabled) {
      if (!isDataLoaded(currentTab)) {
        alert('Please import a change list first.');
      } else if (!isAllCompleted(currentTab)) {
        alert('Please complete all assessment items (EE, PWR, ME, SW, LCM) before reviewing.');
      } else if (!hasSelectedChanges) {
        alert('Please select at least one change item before creating verification cases.');
      }
      return;
    }

    navigate('/verification-review', {
      state: {
        selectedChanges: currentTabData.selectedChanges,
        activeTab: currentTab,
        referenceModels: currentTabData.referenceModels,
        changeListId: currentTabData.changeListId,
        changeListData: currentTabData.changeListData,
        userId: userId,
        subType: currentSubType
      }
    });
  };

  const handleImportClick = () => {
    navigate('/import-change-list', {
      state: {
        returnTab: currentTab,
        selectedChanges: currentTabData.selectedChanges,
        referenceModels: currentTabData.referenceModels,
        completedItems: Array.from(currentTabData.completedItems),
        subType: currentSubType
      }
    });
  };

  // const handleRefreshSavedData = async () => {
  //   if (!userId) {
  //     setLoadingError('User ID not available');
  //     return;
  //   }
    
  //   try {
  //     setLoadingError('');
  //     console.log(`Manually refreshing saved data for ${currentTab}...`);
      
  //     const savedData = await loadSavedAssessmentData(userId, currentTab);
  //     if (savedData) {
  //       console.log(`Successfully refreshed saved data for ${currentTab}`);
  //     } else {
  //       console.log(`No saved data found for ${currentTab}`);
  //     }
  //   } catch (error) {
  //     console.error('Error refreshing saved data:', error);
  //     setLoadingError(`Failed to refresh saved data: ${error.message}`);
  //   }
  // };

  const loading = isLoading(currentTab);
  const dataSource = getDataSource(currentTab);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="sticky top-0 z-20 bg-black/95 backdrop-blur-sm border-b border-gray-600">
        <div className="flex items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center">
            <button
              onClick={handleBackClick}
              className="flex items-center justify-center w-10 h-10 -ml-2 mr-2 
                       rounded-lg hover:bg-gray-800 active:bg-gray-700 
                       transition-colors duration-200 touch-manipulation"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-400 hover:text-white transition-colors" />
            </button>
            <h1 className="text-base font-medium sm:text-lg">HW Change Assessment</h1>
          </div>

          {/* <button
            onClick={handleRefreshSavedData}
            disabled={!userId || loading}
            className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white 
                     hover:bg-gray-800 rounded-lg transition-colors duration-200
                     disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh saved assessment data"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="text-sm">Refresh</span>
          </button> */}
        </div>
      </div>

      <div className="px-4 py-4 sm:px-6 sm:py-6">
        <div className="mb-6">
          <h2 className="mb-3 text-xl font-normal text-white sm:text-2xl md:text-3xl">
            HW Change Verification Assessment
          </h2>
          <p className="text-sm text-gray-400 leading-relaxed sm:text-base max-w-4xl">
            Review changes in the following five blocks: EE, PWR, ME, SW, and LCM, to generate a recommended validation checklist.
          </p>
          
          <div className="mt-4 space-y-2">
            {loading && (
              <div className="p-3 bg-blue-900/50 border border-blue-500 rounded-lg flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-blue-300 flex-shrink-0 animate-spin" />
                <p className="text-blue-300 text-sm">
                  Loading data for {currentSubType || currentTab}...
                </p>
              </div>
            )}

            {loadingError && (
              <div className="p-2 bg-red-900/50 border border-red-500 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-300 flex-shrink-0" />
                <p className="text-red-300 text-sm">{loadingError}</p>
              </div>
            )}

            {!userId && (
              <div className="p-3 border border-yellow-500 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-300 flex-shrink-0" />
                <p className="text-yellow-300 text-sm">User authentication required to load/save data.</p>
              </div>
            )}

            {/* {!loading && !isDataLoaded(currentTab) ? (
              <div className="p-3 bg-blue-900/50 border border-blue-500 rounded-lg">
                <p className="text-blue-300 text-sm">
                  No assessment data for {currentTab}. Import a change list to get started.
                </p>
              </div>
            ) : !loading && isDataLoaded(currentTab) ? (
              <div className="p-3 bg-green-900/50 border border-green-500 rounded-lg">
                <p className="text-green-300 text-sm">
                  Assessment data loaded for {currentTab}: Ready for assessment
                  {dataSource === 'saved' && ' (From previously saved assessment with your selections)'}
                  {dataSource === 'imported' && ' (From newly imported list)'}
                </p>
              </div>
            ) : null} */}

            {/* Show warning when all items are completed but no changes selected */}
            {!loading && isAllCompleted(currentTab) && isDataLoaded(currentTab) && !hasSelectedChanges && (
              <div className="p-1 bg-yellow-900/50 border border-yellow-500 rounded-lg flex items-center gap-2">
                {/* <AlertCircle className="w-4 h-4 text-yellow-300 flex-shrink-0" /> */}
                <p className="text-yellow-300 text-sm">
                  No change applied. Please select at least one change content detail to review and create verification cases.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="mb-3 sm:mb-4">
          <div className="flex border-b border-gray-700 overflow-x-auto scrollbar-thin 
                        scrollbar-thumb-gray-500 scrollbar-track-gray-800">
            {tabs.map((tab, index) => {
              const tabHasData = hasChangeList(tab.id);
              const tabIsLoading = isLoading(tab.id);
              
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  disabled={tabIsLoading}
                  className={`flex-shrink-0 min-w-0 px-4 py-3 text-sm font-medium 
                           border-b-2 transition-all duration-200 touch-manipulation relative
                           ${currentTab === tab.id
                             ? 'border-blue-500 text-white bg-blue-500/10' 
                             : 'border-transparent text-gray-400 hover:text-white hover:bg-gray-800/50 active:bg-gray-700/50'
                           } 
                           ${tabIsLoading ? 'opacity-50 cursor-not-allowed' : ''}
                           ${index < tabs.length - 1 ? 'mr-2 sm:mr-6' : ''}
                           focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 focus:ring-offset-black
                           min-h-[48px] flex items-center justify-center`}
                  aria-selected={currentTab === tab.id}
                  role="tab"
                >
                  <span className="whitespace-nowrap px-2">{tab.label}</span>
                  
                  {/* ✅ UPDATED: Only show loading indicator, removed green dot */}
                  {tabIsLoading && (
                    <div className="absolute -top-1 -right-1">
                      <div className="w-3 h-3 bg-blue-500 rounded-full border-2 border-black animate-pulse" title="Loading"></div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sub-type Dropdown for PD and AVA */}
        {tabs.find(t => t.id === currentTab)?.hasSubTypes && (
          <div className="mb-4 ml-5 sm:ml-3">
            <div className="relative inline-block w-64">
              <button
                onClick={() => setShowSubTypeDropdown(prev => ({ 
                  ...prev, 
                  [currentTab]: !prev[currentTab] 
                }))}
                className="w-full flex items-center justify-between px-4 py-2 bg-gray-800 
                         text-white rounded-lg border border-gray-600 hover:bg-gray-700 
                         focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
              >
                <span className="text-sm">
                  {subProductTypes[currentTab]?.find(st => st.id === currentSubType)?.label || 'Select Type'}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
                  showSubTypeDropdown[currentTab] ? 'rotate-180' : ''
                }`} />
              </button>
              
              {showSubTypeDropdown[currentTab] && (
                <div className="absolute z-30 w-full mt-1 bg-gray-800 border border-gray-600 
                              rounded-lg shadow-xl max-h-60 overflow-auto">
                  {subProductTypes[currentTab]?.map(subType => (
                    <button
                      key={subType.id}
                      onClick={() => handleSubTypeChange(currentTab, subType.id)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-700 
                               transition-colors ${
                                 currentSubType === subType.id 
                                   ? 'bg-blue-600 text-white' 
                                   : 'text-gray-300'
                               }`}
                    >
                      {subType.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="ml-5 sm:ml-3 sm:mb-1">
          <h3 className="text-lg font-normal text-white sm:text-xl md:text-2xl">
            {currentSubType || currentTab}
          </h3>
        </div>

        <div className=" p-3 mb-8 space-y-1 sm:space-y-0 pb-30">
          {assessmentItems.map((item) => {
            const hasData = hasItemData(item);
            const isCompleted = currentTabData.completedItems.has(item);
            
            return (
            <button
              key={`${currentTab}-${item}`}
              onClick={() => handleItemClick(item)}
              disabled={!isDataLoaded(currentTab) || loading}
              className={`group flex items-center justify-between w-full 
                       py-2 px-2 sm:px-0 sm:py-3 
                       border-b border-gray-800 last:border-b-0 
                       hover:bg-gray-900/50 active:bg-gray-800/70
                       focus:bg-gray-900/50 focus:outline-none 
                       focus:ring-2 focus:ring-blue-500/20 focus:ring-inset
                       transition-all duration-200 touch-manipulation
                       rounded-lg sm:rounded-none
                       min-h-[56px] sm:min-h-[64px]
                       transform active:scale-[0.98] sm:active:scale-100
                       ${(!isDataLoaded(currentTab) || loading) ? 'opacity-50 cursor-not-allowed' : ''}`}
              role="button"
              aria-label={`Open ${item} assessment`}
            >
              <span className="text-base font-normal text-white sm:text-lg md:text-xl 
                            group-hover:text-blue-400 transition-colors duration-200">
                {item}
              </span>
              <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12">
                <CheckIcon
                  className={`w-7 h-7 sm:w-8 sm:h-8 transition-all duration-200 
                   ${isCompleted || hasData
                     ? 'text-green-400 scale-110' 
                     : 'text-gray-400 group-hover:text-blue-400 group-active:scale-90'
                   }`}
                  style={{ strokeWidth: 2.5 }}
                />
              </div>
            </button>
          );
          })}
        </div>

        <div className="fixed inset-x-0 bottom-0 z-40 bg-black pt-4 pb-4 space-y-3 sm:space-y-0 sm:flex sm:justify-end sm:gap-4 px-4">
          <button
            onClick={handleImportClick}
            disabled={loading}
            className="w-full sm:w-auto px-6 py-3 text-white bg-gray-600 rounded-lg 
                     hover:bg-gray-700 active:bg-gray-800 focus:bg-gray-700
                     focus:outline-none focus:ring-2 focus:ring-gray-500/20 focus:ring-offset-2 focus:ring-offset-black
                     transition-all duration-200 touch-manipulation
                     min-h-[48px] font-medium text-sm sm:text-base
                     transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
          >
            Import Change List
          </button>
          <button
            className={`w-full sm:w-auto px-6 py-3 text-white rounded-lg 
                     font-medium text-sm sm:text-base min-h-[48px] 
                     transition-all duration-200 touch-manipulation
                     focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-black
                     transform active:scale-[0.98]
                     ${isReviewEnabled
                       ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 focus:bg-blue-700 focus:ring-blue-500/20 shadow-lg hover:shadow-xl' 
                       : 'bg-blue-600/50 cursor-not-allowed'
                     }`}
            type="button"
            disabled={!isReviewEnabled}
            onClick={handleReviewClick}
          >
            Review and Create Verification Cases
          </button>
        </div>
      </div>
    </div>
  );
};

export default HWChangeVerification;
